import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from './ToastContext';
import AuthContext from './AuthContext';
import { cartService } from '../services/cartService';

import { config } from '../config/env';

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState([]);
    const [cartData, setCartData] = useState(null);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const { showToast } = useToast();

    // Defensive: prefer non-throwing check so the app doesn't crash if provider mount order is transiently wrong
    const authCtx = useContext(AuthContext);
    const isAuthenticated = !!authCtx?.isAuthenticated;
    if (process.env.NODE_ENV !== 'production' && !authCtx) {
        console.warn('CartProvider: AuthProvider missing — assuming unauthenticated. Ensure AuthProvider wraps CartProvider.');
    }

    // Fetch cart from server or local storage on mount/auth change
    useEffect(() => {
        if (isAuthenticated) {
            fetchServerCart();
        } else {
            const savedCart = localStorage.getItem('tcr_cart');
            if (savedCart) {
                try {
                    setCartItems(JSON.parse(savedCart));
                    setCartData(null);
                } catch (e) {
                    console.error('Failed to parse cart', e);
                }
            } else {
                setCartItems([]);
                setCartData(null);
            }
        }
    }, [isAuthenticated]);

    // Sync to localStorage only if NOT authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            if (cartItems.length > 0) {
                localStorage.setItem('tcr_cart', JSON.stringify(cartItems));
            } else {
                localStorage.removeItem('tcr_cart');
            }
        }
    }, [cartItems, isAuthenticated]);

    const fetchServerCart = async () => {
        if (!isAuthenticated) return;
        try {
            const response = await cartService.getCart();
            if (response.data.success && response.data.data.cart) {
                // Fix image URLs: prepend base URL if relative
                // config.apiBaseUrl is typically "http://localhost:7005/api/v1"
                // We want "http://localhost:7005"
                let baseUrl = '';
                try {
                    if (config.apiBaseUrl) {
                        const urlObj = new URL(config.apiBaseUrl);
                        baseUrl = urlObj.origin;
                    }
                } catch (e) {
                    console.error('Invalid API Base URL', e);
                }

                const items = response.data.data.cart.items.map(item => {
                    let image = item.image || item.imageUrl; // handle both
                    if (image && typeof image === 'string' && !image.startsWith('http') && !image.startsWith('data:')) {
                        // ensure no double slashes if image starts with /
                        const relativePath = image.startsWith('/') ? image.slice(1) : image;
                        image = `${baseUrl}/${relativePath}`;
                    }

                    return {
                        ...item,
                        image: image
                    };
                });

                setCartItems(items);
                setCartData(response.data.data.cart);
            } else {
                setCartItems([]);
                setCartData(null);
            }
        } catch (error) {
            console.error('Failed to fetch server cart:', error);
            setCartItems([]);
        }
    };

    const syncCart = async () => {
        await fetchServerCart();
    };

    const addToCart = async (product) => {
        const newItem = {
            ...product,
            quantity: product.quantity || 1
        };

        // Optimistically update UI
        // Note: For signed-in users, this temporary item won't have the final _id yet,
        // but it will be replaced by the real server item on the next fetch.
        setCartItems(prev => {
            // Helper function to compare two items for equality
            const areItemsEqual = (item1, item2) => {
                // Must have same base item ID
                if (!((item1.id === item2.id || item1.itemId === item2.id || item1.id === item2.itemId))) {
                    return false;
                }

                // Compare variants/options (handle multiple formats: selectedOptions, selectedVariant, selectedCombination, size)
                const getVariantKey = (item) => {
                    // Try different variant field formats
                    if (item.selectedOptions && Object.keys(item.selectedOptions).length > 0) {
                        return JSON.stringify(item.selectedOptions);
                    }
                    if (item.selectedVariant) {
                        return JSON.stringify(item.selectedVariant);
                    }
                    if (item.selectedCombination) {
                        return JSON.stringify(item.selectedCombination);
                    }
                    if (item.size) {
                        return `size:${item.size}`;
                    }
                    return 'default';
                };

                const variantsMatch = getVariantKey(item1) === getVariantKey(item2);

                // Compare add-ons (handle multiple formats: selectedAddOns, addOns)
                const normalizeAddOns = (item) => {
                    const addOns = item.selectedAddOns || item.addOns || [];
                    if (addOns.length === 0) return '';
                    return addOns
                        .map(a => a._id || a.addOnId || a)
                        .sort()
                        .join(',');
                };

                const addOnsMatch = normalizeAddOns(item1) === normalizeAddOns(item2);

                return variantsMatch && addOnsMatch;
            };

            const existingItemIndex = prev.findIndex(item => areItemsEqual(item, product));

            if (existingItemIndex > -1) {
                return prev.map((item, idx) =>
                    idx === existingItemIndex
                        ? { ...item, quantity: item.quantity + newItem.quantity }
                        : item
                );
            }
            return [...prev, newItem];
        });

        showToast(product.name || product.itemName, 'success', product.image || product.imageUrl, 'Added to Cart');

        if (isAuthenticated) {
            try {
                const payload = {
                    itemId: product.id || product.itemId || product._id,
                    itemType: product.itemType,
                    quantity: newItem.quantity,
                    selectedOptions: product.selectedOptions || {},
                    selectedAddOns: product.selectedAddOns?.map(a => a._id || a) || [],
                    notes: product.notes || '',
                };

                if (product.itemType === 'FoodItemMaster' || product.itemType === 'ComboMaster') {
                    payload.storeId = product.storeId;
                    await cartService.addFoodItem(payload);
                } else if (product.itemType === 'MerchandiseMaster') {
                    await cartService.addMerchandiseItem(payload);
                } else if (product.itemType === 'GiftCardMaster') {
                    payload.itemId = product.masterId || product.id;
                    payload.selectedValue = product.selectedValue;
                    payload.recipient = product.recipient || {};
                    await cartService.addGiftCardItem(payload);
                }

                // Re-fetch to get the real DB IDs
                await fetchServerCart();

            } catch (error) {
                console.error('Failed to sync cart with backend:', error);
                showToast('Failed to save to server cart', 'error');
                // Revert to server state (which implies undoing the optimistic add if it failed)
                fetchServerCart();
            }
        }
    };

    const removeFromCart = async (itemId, size) => {
        // Optimistic update
        const previousItems = [...cartItems];
        setCartItems(prev => prev.filter(item =>
            !((item.id === itemId || item._id === itemId) && (size ? item.size === size : true))
        ));

        if (isAuthenticated) {
            try {
                // itemId passed here logic: 
                // The UI passes `item.id || item._id`. For server items, this is the Cart Item `_id`.
                await cartService.removeItem(itemId);
                // Re-fetch so offer calculations (BOGO etc.) are updated after removal
                await fetchServerCart();
            } catch (error) {
                console.error('Failed to remove item:', error);
                setCartItems(previousItems); // Revert
                showToast('Failed to remove item', 'error');
            }
        }
    };

    const updateQuantity = async (itemId, quantity, size) => {
        if (quantity <= 0) {
            removeFromCart(itemId, size);
            return;
        }

        // Optimistic update
        const previousItems = [...cartItems];
        setCartItems(prev => prev.map(item =>
            ((item.id === itemId || item._id === itemId) && (size ? item.size === size : true))
                ? { ...item, quantity }
                : item
        ));

        if (isAuthenticated) {
            try {
                await cartService.updateItem(itemId, quantity);
            } catch (error) {
                console.error('Failed to update quantity:', error);
                setCartItems(previousItems); // Revert
                showToast('Failed to update quantity', 'error');
            }
        }
    };

    const clearCart = async () => {
        setCartItems([]);
        setCartData(null);
        if (isAuthenticated) {
            try {
                await cartService.clearCart();
            } catch (error) {
                console.error('Failed to clear cart:', error);
            }
        } else {
            localStorage.removeItem('tcr_cart');
        }
    };

    // Calculations handle both formats
    const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
    const cartTotal = cartItems.reduce((acc, item) => {
        const price = item.price || item.basePrice || item.customPrice || 0;
        return acc + (price * item.quantity);
    }, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            cartData,
            cartCount,
            cartTotal,
            addToCart,
            removeFromCart,
            updateQuantity,
            isCartOpen,
            setIsCartOpen,
            clearCart,
            syncCart,
            applySpecialOffer: async (offerId) => {
                console.log('🛒 applySpecialOffer in Context:', { offerId, isAuthenticated, cid: authCtx?.customer?.id || authCtx?.customer?._id });
                if (isAuthenticated && authCtx?.customer) {
                    try {
                        // Always fetch a fresh cart snapshot so we never send stale cartItems
                        // (e.g. right after addToCart completes but before React re-renders with the new state)
                        let freshCartItems = cartItems;
                        try {
                            const cartRes = await cartService.getCart();
                            if (cartRes.data?.data?.cart?.items) {
                                freshCartItems = cartRes.data.data.cart.items;
                            }
                        } catch {
                            // fall back to state cartItems if getCart fails
                        }
                        const res = await cartService.applySpecialOffer(offerId, authCtx.customer.id || authCtx.customer._id, freshCartItems);
                        await fetchServerCart();
                        return res.data; // Return data so caller can handle action === "SELECT_FREE_ITEM" / "SELECT_X_ITEM"
                    } catch (error) {
                        console.error('❌ Failed to apply special offer:', error);
                        return { isOk: false, message: error.response?.data?.message || 'Error' };
                    }
                } else {
                    console.warn('⚠️ applySpecialOffer skipped: User not authenticated');
                    authCtx?.openAuthModal?.();
                    return { isOk: false, message: 'Not authenticated' };
                }
            },
            removeSpecialOffer: async (offerId) => {
                console.log('🛒 removeSpecialOffer in Context:', { offerId, isAuthenticated, cid: authCtx?.customer?.id || authCtx?.customer?._id });
                if (isAuthenticated && authCtx?.customer) {
                    try {
                        await cartService.removeSpecialOffer(offerId, authCtx.customer.id || authCtx.customer._id);
                        await fetchServerCart();
                    } catch (error) {
                        console.error('❌ Failed to remove special offer:', error);
                    }
                }
            }
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

